import React, { useState, useRef, useEffect } from "react";

/**
 * LazyRow - Defers rendering of showcase row children until they are
 * near the viewport. Uses IntersectionObserver with a generous rootMargin
 * so content loads just before the user scrolls to it.
 *
 * The first `eagerCount` rows (by parentIndex) render immediately to
 * ensure above-the-fold content appears instantly.
 *
 * Once a row becomes visible it stays rendered permanently to preserve
 * spatial-navigation state and avoid layout thrashing.
 *
 * @param {React.ReactNode} children  - The actual row component to render
 * @param {number} index              - The parentIndex of this row in the showcase list
 * @param {number} eagerCount         - How many rows to render eagerly (default 12)
 * @param {number} minHeight          - Placeholder height in px (default 400)
 */
const LazyRow = ({ children, index, eagerCount = 12, minHeight = 400 }) => {
    const isEager = index < eagerCount;
    const [isVisible, setIsVisible] = useState(isEager);
    const placeholderRef = useRef(null);

    useEffect(() => {
        // Already visible (eager or previously intersected) — nothing to do
        if (isVisible) return;

        const node = placeholderRef.current;
        if (!node) return;

        if (typeof IntersectionObserver === "undefined") {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect(); // Once visible, never revert
                }
            },
            {
                // Start loading when the row is within two TV viewport heights.
                rootMargin: "1400px 0px",
                threshold: 0,
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [isVisible]);

    if (isVisible) {
        return <>{children}</>;
    }

    // Placeholder keeps scroll height stable for spatial nav
    return (
        <div
            ref={placeholderRef}
            style={{ minHeight: `${minHeight}px` }}
            aria-hidden="true"
        />
    );
};

export default LazyRow;
